import React, { useState, useEffect } from 'react';
import {
  Button,
  Grid,
  MenuItem,
  TextField,
  Typography,
  CircularProgress
} from '@mui/material';
import { useForm, SubmitHandler, Controller } from 'react-hook-form';
import { createDesignation } from 'src/services/DesignationService';
import { getAllDepartments } from 'src/services/employeeService';
import { IDesignations } from 'src/content/Designation/interface/designation';
import { ETrackOptions } from './enum/ETrackOptions';
import { ETierOptions } from './enum/ETierOptions';
import SnackBar from 'src/components/SnackBar/SnackBar';

type AddDesignationProps = {
  onClose: () => void;
  onSubmit: (data: IDesignations) => void;
};

interface Department {
  id: number;
  name: string;
}

const trackOptions = [
  ETrackOptions.TECHNOLOGY,
  ETrackOptions.BUSINESS_CONSULTING,
  ETrackOptions.SHARED_SERVICE,
  ETrackOptions.QUALITY_ASSURANCE,
  ETrackOptions.PROJECT_MANAGEMENT,
] as const;

const tierOptions = [
  ETierOptions.T1,
  ETierOptions.T2,
  ETierOptions.T3,
  ETierOptions.T4,
  ETierOptions.T5
] as const;

const AddDesignation: React.FC<AddDesignationProps> = ({ onClose, onSubmit }) => {
  const [submitting, setSubmitting] = useState(false);
  const [snackBarOpen, setSnackBarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState('');
  const [snackBarMessage, setSnackBarMessage] = useState('');
  const [shouldCloseAfterSnackbar, setShouldCloseAfterSnackbar] = useState(false);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [loadingDepartments, setLoadingDepartments] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    control,
    getValues,
    formState: { errors },
  } = useForm<IDesignations>({
    defaultValues: {
      name: '',
      code: '',
      track: '',
      tier: '',
      departmentId: undefined,
    },
  });

  // Fetch departments on component mount
  useEffect(() => {
    const fetchDepartments = async () => {
      setLoadingDepartments(true);
      try {
        const response = await getAllDepartments();
        setDepartments(response.data || []);
      } catch (error) {
        console.error('Error fetching departments:', error);
        setSnackBarMessage('Failed to load departments.');
        setSnackbarSeverity('error');
        setSnackBarOpen(true);
      } finally {
        setLoadingDepartments(false);
      }
    };

    fetchDepartments();
  }, []);

  const onFormSubmit: SubmitHandler<IDesignations> = async (formData) => {
    const trimmedName = formData.name.trim().toLocaleLowerCase();
    const trimmedCode = formData.code.trim();

    if (!trimmedName || !trimmedCode) {
      setSnackBarMessage('Name and Code cannot be empty or spaces only.');
      setSnackbarSeverity('error');
      setSnackBarOpen(true);
      return;
    }

    setSubmitting(true);

    const payload = {
      name: trimmedName,
      code: trimmedCode,
      track: formData.track,
      tier: formData.tier,
      departmentId: formData.departmentId,
    };

    try {
      const res = await createDesignation(payload);
      if (res.status === 200 || res.status === 201) {
        onSubmit(payload);
        reset();
        onClose(); // Close on success
      } else {
        setSnackBarMessage('Failed to create designation.');
        setSnackbarSeverity('error');
        setSnackBarOpen(true);
      }
    } catch (error: any) {
      if (error?.response?.status === 400) {
        setSnackBarMessage('A designation with the same name already exists.');
      } else {
        setSnackBarMessage('An error occurred while creating designation.');
      }
      setSnackbarSeverity('error');
      setSnackBarOpen(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit(onFormSubmit)}>
        <Grid container spacing={2} sx={{ p: 3 }}>
          <Grid item xs={12}>
            <Typography fontWeight="bold" fontSize="16px">Add Designation</Typography>
          </Grid>

          {/* Name */}
          <Grid item xs={6}>
            <Typography sx={{ pl: 1, fontSize: '13px' }}>
              Name<span style={{ color: 'red' }}> *</span>
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="Enter Name"
              {...register('name', {
                required: 'Name is required.',
                validate: value => value.trim() !== '' || 'Name cannot be empty or spaces only.'
              })}
            />
            {errors.name && (
              <Typography sx={{ pl: 1, fontSize: '11px' }} color="red">
                {errors.name.message}
              </Typography>
            )}
          </Grid>

          {/* Code */}
          <Grid item xs={6}>
            <Typography sx={{ pl: 1, fontSize: '13px' }}>
              Code<span style={{ color: 'red' }}> *</span>
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="Enter Code"
              {...register('code', {
                required: 'Code is required.',
                validate: value => value.trim() !== '' || 'Code cannot be empty or spaces only.'
              })}
            />
            {errors.code && (
              <Typography sx={{ pl: 1, fontSize: '11px' }} color="red">
                {errors.code.message}
              </Typography>
            )}
          </Grid>

          {/* Department */}
          <Grid item xs={6}>
            <Typography sx={{ pl: 1, fontSize: '13px' }}>
              Department<span style={{ color: 'red' }}> *</span>
            </Typography>
            <Controller
              name="departmentId"
              control={control as any}
              rules={{ required: 'Department is required.' }}
              render={({ field, fieldState }) => (
                <>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    value={field.value || ''}
                    onChange={(e) => field.onChange(Number(e.target.value))}
                    onBlur={field.onBlur}
                    inputRef={field.ref}
                    disabled={loadingDepartments}
                    SelectProps={{
                      displayEmpty: true,
                      renderValue: (selected) => {
                        if (!selected) return 'Select Department';
                        const dept = departments.find(d => d.id === selected);
                        return dept ? dept.name : String(selected);
                      }
                    }}
                  >
                    {loadingDepartments ? (
                      <MenuItem disabled>
                        <CircularProgress size={20} /> Loading...
                      </MenuItem>
                    ) : (
                      departments.map((department) => (
                        <MenuItem key={department.id} value={department.id}>
                          {department.name}
                        </MenuItem>
                      ))
                    )}
                  </TextField>
                  {fieldState.error && (
                    <Typography sx={{ pl: 1, fontSize: '11px' }} color="red">
                      {fieldState.error.message}
                    </Typography>
                  )}
                </>
              )}
            />
          </Grid>

          {/* Track */}
          <Grid item xs={6}>
            <Typography sx={{ pl: 1, fontSize: '13px' }}>
              Track<span style={{ color: 'red' }}> *</span>
            </Typography>
            <Controller
              name="track"
              control={control as any}
              rules={{ required: 'Track is required.' }}
              render={({ field, fieldState }) => (
                <>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    value={field.value || ''}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    inputRef={field.ref}
                    SelectProps={{
                      displayEmpty: true,
                      renderValue: (selected) => selected !== '' ? selected : 'Select Track'
                    }}
                  >
                    {trackOptions.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </TextField>
                  {fieldState.error && (
                    <Typography sx={{ pl: 1, fontSize: '11px' }} color="red">
                      {fieldState.error.message}
                    </Typography>
                  )}
                </>
              )}
            />
          </Grid>

          {/* Tier */}
          <Grid item xs={6}>
            <Typography sx={{ pl: 1, fontSize: '13px' }}>
              Tier<span style={{ color: 'red' }}> *</span>
            </Typography>
            <Controller
              name="tier"
              control={control as any}
              rules={{ required: 'Tier is required.' }}
              render={({ field, fieldState }) => (
                <>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    value={field.value || ''}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    inputRef={field.ref}
                    SelectProps={{
                      displayEmpty: true,
                      renderValue: (selected) => selected !== '' ? selected : 'Select Tier'
                    }}
                  >
                    {tierOptions.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </TextField>
                  {fieldState.error && (
                    <Typography sx={{ pl: 1, fontSize: '11px' }} color="red">
                      {fieldState.error.message}
                    </Typography>
                  )}
                </>
              )}
            />
          </Grid>

          {/* Buttons */}
          <Grid item xs={12} sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button onClick={onClose} sx={{ mr: 2 }} variant="outlined" color="secondary" disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" color="primary" disabled={submitting}>
              {submitting ? <CircularProgress size={24} /> : 'Submit'}
            </Button>
          </Grid>
        </Grid>
      </form>

      {/* Snackbar */}
      <SnackBar
        snackBarOpen={snackBarOpen}
        setSnackBarOpen={setSnackBarOpen}
        snackBarMessage={snackBarMessage}
        snackbarSeverity={snackbarSeverity}
        isClickContinue={() => {
          if (shouldCloseAfterSnackbar) {
            onClose();
          }
        }}
      />
    </>
  );
};

export default AddDesignation;